import CheckCircle2 from 'lucide-react/dist/esm/icons/check-circle-2'
import Circle from 'lucide-react/dist/esm/icons/circle'

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { scrollToVerificationTarget } from '@/utils/scrollToVerificationTarget'

export interface VerificationChecklistItem {
  label: string
  done: boolean
  /** DOM id of the matching verification card to scroll to when the row is clicked. */
  targetId?: string
}

interface ProgressSummaryCardProps {
  items: VerificationChecklistItem[]
  isLoading: boolean
  /** Row count for the loading skeleton when `items` is still empty. */
  expectedItemCount?: number
}

const RING_RADIUS = 32
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

const CHECKLIST_ROW_CLASS = 'flex items-center gap-2'

/** Sidebar summary with percent ring and a checklist of uploaded vs outstanding items. */
export function ProgressSummaryCard({
  items,
  isLoading,
  expectedItemCount = 4,
}: ProgressSummaryCardProps) {
  const completed = items.filter((item) => item.done).length
  const total = items.length > 0 ? items.length : expectedItemCount
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0
  const complete = !isLoading && items.length > 0 && completed >= items.length
  const dashOffset = isLoading
    ? RING_CIRCUMFERENCE
    : RING_CIRCUMFERENCE - (percent / 100) * RING_CIRCUMFERENCE
  const skeletonRows = items.length > 0 ? items.length : expectedItemCount

  return (
    <div className="rounded-2xl border border-border/60 bg-white p-5 shadow-[0_2px_16px_rgba(0,0,0,0.04)]">
      <div className="flex h-[76px] items-center gap-4">
        <div className="relative h-[76px] w-[76px] shrink-0">
          <svg className="h-full w-full -rotate-90" viewBox="0 0 76 76" aria-hidden="true">
            <circle cx="38" cy="38" r={RING_RADIUS} fill="none" strokeWidth="6" className="stroke-muted" />
            <circle
              cx="38"
              cy="38"
              r={RING_RADIUS}
              fill="none"
              strokeWidth="6"
              strokeLinecap="round"
              className={complete ? 'stroke-success-border' : 'stroke-primary'}
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              style={{ transition: 'stroke-dashoffset 600ms ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            {complete ? (
              <CheckCircle2 className="h-7 w-7 text-success-border" />
            ) : (
              <span className="flex h-5 w-10 items-center justify-center text-lg font-bold leading-none text-foreground">
                {isLoading ? '' : `${percent}%`}
              </span>
            )}
            {isLoading && !complete ? (
              <Skeleton className="absolute h-5 w-10 rounded-md" aria-hidden="true" />
            ) : null}
          </div>
        </div>

        <div className="min-w-0">
          <p className="h-5 text-sm font-semibold leading-none text-foreground">
            {complete ? "You're all set" : 'Verification in progress'}
          </p>
          <div className="mt-1 h-4 text-xs leading-4 text-muted-foreground">
            {isLoading ? (
              <Skeleton className="inline-block h-3 w-28 align-middle" aria-hidden="true" />
            ) : (
              `${completed} of ${total} complete`
            )}
          </div>
        </div>
      </div>

      <ul
        className="mt-4 space-y-2 border-t border-border/50 pt-4"
        aria-busy={isLoading}
        aria-label="Verification checklist"
      >
        {isLoading
          ? Array.from({ length: skeletonRows }, (_, index) => (
              <li key={index} className={CHECKLIST_ROW_CLASS} aria-hidden="true">
                <Skeleton className="h-4 w-4 shrink-0 rounded-full" />
                <Skeleton className="h-3 max-w-[85%] flex-1" />
              </li>
            ))
          : items.map((item) => {
              const rowContent = (
                <>
                  {item.done ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-success-border" aria-hidden="true" />
                  ) : (
                    <Circle className="h-4 w-4 shrink-0 text-muted-foreground/50" aria-hidden="true" />
                  )}
                  <span
                    className={cn(
                      'text-xs leading-snug',
                      item.done ? 'text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {item.label}
                  </span>
                </>
              )

              return (
                <li key={item.label} className={item.targetId ? undefined : CHECKLIST_ROW_CLASS}>
                  {item.targetId ? (
                    <button
                      type="button"
                      onClick={() => scrollToVerificationTarget(item.targetId!)}
                      className={cn(
                        CHECKLIST_ROW_CLASS,
                        'w-full border-0 bg-transparent p-0 text-left transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                      )}
                    >
                      {rowContent}
                    </button>
                  ) : (
                    rowContent
                  )}
                </li>
              )
            })}
      </ul>
    </div>
  )
}
